import ConfirmDialog from "@/components/ui/ConfirmDialog";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** User losing the role */
  previousHolderName: string;
  roleLabel: string;
  /** New assignee */
  incomingName: string;
  /** "lead" or "this deal" */
  placementLabel: string;
  isPending?: boolean;
};

/**
 * Same-role replacement — uses the app-wide {@link ConfirmDialog} shell
 * (Delete Stage / Block user pattern).
 *
 * ROLE-BASED FOLLOW-UPS: a hand-over always resets the role's follow-up to
 * TODAY (user rule 2026-09-08 — "if we reassign to a different counsellor the
 * follow-up should be the current day, no matter what"). The server does this
 * for every assignment that changes hands, on every path (drawer, bulk,
 * automation), so the dialog no longer asks keep-vs-fresh — it only tells the
 * operator what will happen.
 */
export default function SameRoleReplaceConfirmModal({
  open,
  onClose,
  onConfirm,
  previousHolderName,
  roleLabel,
  incomingName,
  placementLabel,
  isPending,
}: Props) {
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Same role already assigned"
      tone="warning"
      warningTitle="The current assignee will lose access"
      warningDescription={
        <>
          <span className="font-semibold">{previousHolderName}</span> currently
          holds the <span className="font-semibold">{roleLabel}</span> role on{" "}
          {placementLabel}. If you continue, they will no longer see this lead
          in their assignment views for that role, and{" "}
          <span className="font-semibold">{incomingName}</span> will take over.
        </>
      }
      confirmLabel="Revoke & assign"
      loadingLabel="Working…"
      isLoading={isPending}
      size="md"
    >
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--color-text-gray)]">
          Their follow-up
        </span>
        <p className="text-[12px] leading-snug text-[var(--color-text-primary)]">
          The scheduled follow-up is closed out and a new one due{" "}
          <span className="font-semibold">today</span> is opened for{" "}
          {incomingName}.
        </p>
      </div>
    </ConfirmDialog>
  );
}
